import React from 'react';
import 'katex/dist/katex.min.css';
import katex from 'katex';
import {
  PlayIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  BoltIcon,
  VariableIcon,
  BookOpenIcon,
  FilmIcon,
} from '@heroicons/react/24/outline';

// ── types (subset of the structured LectureDigest) ──────────────────────────
interface Step { do?: string; why?: string; subgoal?: string }
interface Row { aspect?: string; values?: string[] }
export interface Block {
  type: string;
  anchor_sec?: number;
  title?: string;
  body?: string;
  concrete_example?: string;
  why_it_matters?: string;
  latex?: string;
  usage_cue?: string;
  conditions?: string;
  problem?: string;
  given?: string[];
  asked?: string;
  method?: string;
  common_trap?: string;
  steps?: Step[];
  answer?: string;
  compared?: string[];
  rows?: Row[];
  caption?: string;
}
export interface Topic {
  title: string;
  start_sec: number;
  end_sec: number;
  summary?: string;
  cues?: string[];
  takeaway?: string;
  blocks?: Block[];
}
export interface Digest {
  title?: string;
  tldr?: string;
  objectives?: string[];
  topics?: Topic[];
  formula_sheet?: Block[];
  key_terms?: Block[];
  common_pitfalls?: Block[];
  visuals?: Block[];
}

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');
const fmt = (s?: number): string => {
  if (!s || s < 0 || !isFinite(s)) return '0:00';
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};
const secId = (s?: number) => (s && s > 0 ? `sec-${Math.floor(s)}` : undefined);

// ── math renderer: digest fields are plain text + \(..\)/\[..\] LaTeX. Render the
//    math with KaTeX directly (escape the surrounding text so it's XSS-safe). ──
const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const renderTex = (raw: string): string => {
  const re = /\\\(([\s\S]+?)\\\)|\\\[([\s\S]+?)\\\]/g;
  let out = '', last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    out += escapeHtml(raw.slice(last, m.index));
    const tex = (m[1] ?? m[2]) || '';
    try { out += katex.renderToString(tex, { displayMode: m[2] != null, throwOnError: false, output: 'html' }); }
    catch { out += escapeHtml(m[0]); }
    last = m.index + m[0].length;
  }
  out += escapeHtml(raw.slice(last));
  return out;
};
const T: React.FC<{ text?: string; className?: string }> = ({ text, className }) =>
  text ? <span className={cx('[&_.katex-display]:my-1.5', className)} dangerouslySetInnerHTML={{ __html: renderTex(text) }} /> : null;

const TsPill: React.FC<{ sec?: number; seek: (s: number) => void }> = ({ sec, seek }) =>
  sec && sec > 0 ? (
    <button onClick={() => seek(sec)} title="Jump to this moment"
      className="inline-flex flex-none items-center gap-0.5 rounded-md bg-cyan-500/10 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-cyan-600 transition hover:bg-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/60 dark:text-cyan-300">
      <PlayIcon className="h-2.5 w-2.5" />{fmt(sec)}
    </button>
  ) : null;

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2.5">
    <span className="flex-none pt-px text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</span>
    <div className="min-w-0 flex-1 text-slate-700 dark:text-slate-200">{children}</div>
  </div>
);

const Callout: React.FC<{ tone: 'red' | 'amber' | 'cyan'; icon: any; label: string; children: React.ReactNode }> = ({ tone, icon: Icon, label, children }) => {
  const tones = {
    red: 'border-red-500/25 bg-red-500/[0.06]',
    amber: 'border-amber-500/25 bg-amber-500/[0.06]',
    cyan: 'border-cyan-500/25 bg-cyan-500/[0.05]',
  }[tone];
  const head = { red: 'text-red-600 dark:text-red-300', amber: 'text-amber-700 dark:text-amber-300', cyan: 'text-cyan-700 dark:text-cyan-300' }[tone];
  return (
    <div className={cx('rounded-lg border px-3 py-2', tones)}>
      <div className={cx('mb-0.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide', head)}><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className="text-sm text-slate-700 dark:text-slate-200">{children}</div>
    </div>
  );
};

// ── block renderers ─────────────────────────────────────────────────────────
const WorkedExample: React.FC<{ b: Block; seek: (s: number) => void }> = ({ b, seek }) => (
  <div id={secId(b.anchor_sec)} className="scroll-mt-4 overflow-hidden rounded-xl border border-cyan-500/25 bg-white shadow-sm dark:bg-cyan-500/[0.03]">
    <div className="flex items-center justify-between gap-2 border-b border-cyan-500/15 bg-cyan-500/[0.07] px-3 py-2">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-cyan-700 dark:text-cyan-300">
        <VariableIcon className="h-3.5 w-3.5" />Worked example
      </span>
      <TsPill sec={b.anchor_sec} seek={seek} />
    </div>
    <div className="space-y-3 p-3.5 text-sm">
      {b.title && <p className="font-bold text-slate-900 dark:text-white">{b.title}</p>}
      {b.problem && <Field label="Problem"><T text={b.problem} /></Field>}
      {b.given && b.given.length > 0 && (
        <Field label="Given">
          <ul className="grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
            {b.given.map((g, i) => <li key={i} className="flex gap-1.5"><span className="text-cyan-500">·</span><T text={g} /></li>)}
          </ul>
        </Field>
      )}
      {b.asked && <Field label="Find"><T text={b.asked} /></Field>}
      {b.method && <Field label="Method"><T text={b.method} /></Field>}
      {b.steps && b.steps.length > 0 && (
        <ol className="space-y-2.5 pl-1">
          {b.steps.map((s, i) => (
            <li key={i} className="flex gap-2.5">
              <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-cyan-500/15 text-[10px] font-bold text-cyan-600 dark:text-cyan-300">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <T text={s.do} className="text-slate-800 dark:text-slate-100" />
                {s.why && <span className="mt-0.5 block text-xs italic text-slate-500 dark:text-slate-400"><T text={s.why} /></span>}
                {s.subgoal && <span className="mt-1 inline-block rounded bg-slate-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{s.subgoal}</span>}
              </div>
            </li>
          ))}
        </ol>
      )}
      {b.answer && (
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-lg bg-cyan-500/10 px-3 py-2">
          <span className="text-[11px] font-bold uppercase tracking-wide text-cyan-700 dark:text-cyan-300">Answer</span>
          <span className="font-semibold text-slate-900 dark:text-white"><T text={b.answer} /></span>
        </div>
      )}
      {b.common_trap && <Callout tone="amber" icon={ExclamationTriangleIcon} label="Common trap"><T text={b.common_trap} /></Callout>}
    </div>
  </div>
);

const FormulaCard: React.FC<{ b: Block; seek: (s: number) => void; sheet?: boolean }> = ({ b, sheet }) => (
  <div className={cx('rounded-lg border border-slate-500/15 bg-slate-500/[0.04] px-3 py-2.5', !sheet && 'scroll-mt-4')} id={!sheet ? secId(b.anchor_sec) : undefined}>
    {b.title && <div className="mb-1 text-xs font-semibold text-slate-500 dark:text-slate-400">{b.title}</div>}
    <div className="overflow-x-auto py-0.5 text-[15px]"><T text={b.latex} /></div>
    {b.usage_cue && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400"><span className="font-semibold">Use when:</span> <T text={b.usage_cue} /></p>}
    {b.conditions && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400"><span className="font-semibold">Valid when:</span> <T text={b.conditions} /></p>}
  </div>
);

const Comparison: React.FC<{ b: Block; seek: (s: number) => void }> = ({ b }) => (
  <div id={secId(b.anchor_sec)} className="scroll-mt-4">
    {b.title && <p className="mb-1.5 text-sm font-semibold">{b.title}</p>}
    <div className="overflow-x-auto rounded-lg border border-slate-500/15">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-500/[0.06] text-left">
            <th className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">Aspect</th>
            {(b.compared || []).map((c, i) => <th key={i} className="px-3 py-1.5 font-semibold"><T text={c} /></th>)}
          </tr>
        </thead>
        <tbody>
          {(b.rows || []).map((r, i) => (
            <tr key={i} className="border-t border-slate-500/10">
              <td className="px-3 py-1.5 font-medium text-slate-500 dark:text-slate-400"><T text={r.aspect} /></td>
              {(r.values || []).map((v, j) => <td key={j} className="px-3 py-1.5"><T text={v} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const StepsCard: React.FC<{ b: Block; seek: (s: number) => void; label: string }> = ({ b, seek, label }) => (
  <div id={secId(b.anchor_sec)} className="scroll-mt-4 rounded-lg border border-slate-500/15 bg-slate-500/[0.03] p-3 text-sm">
    <div className="mb-1.5 flex items-center justify-between gap-2">
      <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}{b.title ? ` · ${b.title}` : ''}</span>
      <TsPill sec={b.anchor_sec} seek={seek} />
    </div>
    {b.body && <div className="mb-1.5"><T text={b.body} /></div>}
    <ol className="space-y-1.5 pl-1">
      {(b.steps || []).map((s, i) => (
        <li key={i} className="flex gap-2.5">
          <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-slate-500/15 text-[10px] font-bold text-slate-500 dark:text-slate-300">{i + 1}</span>
          <div className="min-w-0 flex-1">
            <T text={s.do} />
            {s.why && <span className="mt-0.5 block text-xs italic text-slate-500 dark:text-slate-400"><T text={s.why} /></span>}
          </div>
        </li>
      ))}
    </ol>
    {b.conditions && <p className="mt-1.5 text-xs italic text-slate-500 dark:text-slate-400">Assumptions: <T text={b.conditions} /></p>}
  </div>
);

const Concept: React.FC<{ b: Block; seek: (s: number) => void }> = ({ b }) => (
  <div className="text-sm">
    {b.title && <p className="font-semibold">{b.title}</p>}
    <T text={b.body} className="text-slate-700 dark:text-slate-300" />
    {b.concrete_example && <p className="mt-1 text-slate-500 dark:text-slate-400"><span className="font-semibold text-cyan-600 dark:text-cyan-400">e.g.</span> <T text={b.concrete_example} /></p>}
    {b.why_it_matters && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400"><span className="font-semibold">Why it matters:</span> <T text={b.why_it_matters} /></p>}
  </div>
);

const BlockView: React.FC<{ b: Block; seek: (s: number) => void }> = ({ b, seek }) => {
  switch (b.type) {
    case 'worked_example': return <WorkedExample b={b} seek={seek} />;
    case 'formula': return <FormulaCard b={b} seek={seek} />;
    case 'comparison': return <Comparison b={b} seek={seek} />;
    case 'derivation': return <StepsCard b={b} seek={seek} label="Derivation" />;
    case 'procedure': return <StepsCard b={b} seek={seek} label="Method" />;
    case 'concept': return <Concept b={b} seek={seek} />;
    case 'definition': return <p className="text-sm"><span className="font-semibold text-slate-900 dark:text-white">{b.title}</span> — <span className="text-slate-600 dark:text-slate-300"><T text={b.body} /></span></p>;
    case 'pitfall': return <Callout tone="red" icon={ExclamationTriangleIcon} label="Pitfall"><T text={b.body} /></Callout>;
    case 'intuition': return <Callout tone="cyan" icon={LightBulbIcon} label="Intuition"><T text={b.body} /></Callout>;
    case 'heuristic': return <Callout tone="amber" icon={BoltIcon} label="Rule of thumb"><T text={b.body} /></Callout>;
    case 'visual': return (
      <button onClick={() => b.anchor_sec && seek(b.anchor_sec)} id={secId(b.anchor_sec)}
        className="scroll-mt-4 flex w-full items-center gap-2 rounded-lg border border-dashed border-cyan-500/30 bg-cyan-500/[0.04] px-3 py-2 text-left text-sm transition hover:bg-cyan-500/10">
        <FilmIcon className="h-4 w-4 flex-none text-cyan-500" />
        <span className="min-w-0 flex-1 truncate text-slate-600 dark:text-slate-300">{b.caption || 'Diagram'}</span>
        <span className="flex-none text-[11px] font-semibold tabular-nums text-cyan-600 dark:text-cyan-300">▸ {fmt(b.anchor_sec)}</span>
      </button>
    );
    default: return <T text={b.body} className="text-sm" />;
  }
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-2 mt-1 text-[11px] font-bold uppercase tracking-[0.14em] text-cyan-600 dark:text-cyan-400">{children}</div>
);

// ── main ────────────────────────────────────────────────────────────────────
const DigestNotes: React.FC<{ digest: Digest; seek: (s: number) => void; dark?: boolean; activeSec?: number }> = ({ digest, seek, activeSec }) => {
  const topics = digest.topics || [];
  const activeFloor = typeof activeSec === 'number' ? Math.floor(activeSec) : -1;
  return (
    <div className="space-y-7 px-4 py-4">
      <header>
        {digest.title && <h1 className="text-lg font-extrabold tracking-tight">{digest.title}</h1>}
        {digest.tldr && <p className="mt-1.5 text-sm leading-relaxed text-slate-600 dark:text-slate-300"><span className="font-semibold text-slate-900 dark:text-white">TL;DR </span><T text={digest.tldr} /></p>}
      </header>

      {topics.map((t, i) => {
        const isActive = Math.floor(t.start_sec) === activeFloor;
        return (
        <section key={`${t.start_sec}-${i}`} id={secId(t.start_sec)}
          className={cx('-mx-3 scroll-mt-4 rounded-xl px-3 py-2 transition-colors', isActive && 'bg-cyan-500/[0.06] ring-1 ring-cyan-500/20')}>
          <div className="mb-2.5 flex items-start gap-2.5">
            <span className={cx('mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs font-bold', isActive ? 'bg-cyan-500 text-white' : 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-300')}>{i + 1}</span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-bold tracking-tight">{t.title}</h2>
                <TsPill sec={t.start_sec} seek={seek} />
              </div>
              {t.summary && <p className="mt-0.5 text-sm leading-relaxed text-slate-600 dark:text-slate-300"><T text={t.summary} /></p>}
            </div>
          </div>

          {t.cues && t.cues.length > 0 && (
            <div className="mb-3 ml-[34px] rounded-lg border border-slate-500/15 bg-slate-500/[0.03] px-3 py-2">
              <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Test yourself</div>
              <ul className="space-y-1">
                {t.cues.map((c, j) => <li key={j} className="flex gap-2 text-sm text-slate-600 dark:text-slate-300"><span className="font-bold text-cyan-500">?</span><span><T text={c} /></span></li>)}
              </ul>
            </div>
          )}

          <div className="ml-[34px] space-y-3">
            {(t.blocks || []).map((b, j) => <BlockView key={j} b={b} seek={seek} />)}
          </div>

          {t.takeaway && (
            <div className="ml-[34px] mt-3 border-l-2 border-cyan-500 pl-3 text-sm font-medium italic text-slate-600 dark:text-slate-300"><T text={t.takeaway} /></div>
          )}
        </section>
        );
      })}

      {digest.formula_sheet && digest.formula_sheet.length > 0 && (
        <section id="formula-sheet" className="scroll-mt-4">
          <SectionLabel>Formula sheet</SectionLabel>
          <div className="space-y-2">{digest.formula_sheet.map((b, i) => <FormulaCard key={i} b={b} seek={seek} sheet />)}</div>
        </section>
      )}

      {digest.common_pitfalls && digest.common_pitfalls.length > 0 && (
        <section className="scroll-mt-4">
          <SectionLabel>Common mistakes</SectionLabel>
          <div className="space-y-2">{digest.common_pitfalls.map((b, i) => (
            <Callout key={i} tone="red" icon={ExclamationTriangleIcon} label="Pitfall"><T text={b.body || b.title} /></Callout>
          ))}</div>
        </section>
      )}

      {digest.key_terms && digest.key_terms.length > 0 && (
        <section id="key-terms" className="scroll-mt-4">
          <SectionLabel>Key terms</SectionLabel>
          <dl className="space-y-2">{digest.key_terms.map((b, i) => (
            <div key={i} className="flex gap-2 text-sm">
              <BookOpenIcon className="mt-0.5 h-4 w-4 flex-none text-cyan-500" />
              <div><dt className="inline font-semibold text-slate-900 dark:text-white">{b.title}</dt>
                <dd className="inline text-slate-600 dark:text-slate-300"> — <T text={b.body} /></dd></div>
            </div>
          ))}</dl>
        </section>
      )}

      {digest.visuals && digest.visuals.length > 0 && (
        <section className="scroll-mt-4">
          <SectionLabel>Diagrams in this lecture</SectionLabel>
          <p className="mb-2 text-xs text-slate-400">Click to watch the tutor draw it.</p>
          <div className="space-y-1.5">{digest.visuals.map((b, i) => <BlockView key={i} b={{ ...b, type: 'visual' }} seek={seek} />)}</div>
        </section>
      )}
    </div>
  );
};

export default DigestNotes;
